// Storage key for the staged gift-card Idempotency-Keys. Separate from
// `conversation_cache` on purpose: that one holds draft message text and is cleared on
// every send, while this must survive a reload of a page whose request may have funded a
// card.
const GIFT_CARD_IDEMPOTENCY_STORAGE_KEY = 'gift_card_idempotency'

// Matches the server's retention (ISSUE_IDEMPOTENCY_WINDOW_SECONDS, 24h). Past it the
// backend has forgotten the key, so replaying it would issue a second card rather than
// return the first response — exactly what holding it is supposed to prevent.
const GIFT_CARD_IDEMPOTENCY_TTL_MS = 24 * 60 * 60 * 1000

function pruneExpired(entries) {
  const now = Date.now()
  return Object.entries(entries || {}).reduce((kept, [customerId, entry]) => {
    if (entry?.createdAt && now - entry.createdAt < GIFT_CARD_IDEMPOTENCY_TTL_MS) {
      kept[customerId] = entry
    }
    return kept
  }, {})
}

export default {
  namespaced: true,
  state: {
    isSavingCache: false,
    conversationCache: {},
    // { [customerId]: { key, amountCents, createdAt } }
    giftCardIdempotency: {}
  },
  mutations: {
    SET_MESSAGE_CACHE(state, message) {
      state.conversationCache = message
    },
    SET_IS_SAVING_CACHE(state, isSaving) {
      state.isSavingCache = isSaving
    },
    SET_GIFT_CARD_IDEMPOTENCY(state, entries) {
      state.giftCardIdempotency = entries
    }
  },
  getters: {
    selectConversationCache: (state) => state.conversationCache
  },
  actions: {
    async initMessageCache({ commit }) {
      try {
        const cache = await localStorage.getItem('conversation_cache')
        if (!cache) return
        commit('SET_MESSAGE_CACHE', JSON.parse(cache))
      } catch (error) {
        console.error(error)
      }
    },

    async setMessageCache({ commit, state }, { conversationId, message }) {
      try {
        if (!conversationId || !message || state.isSavingCache) return

        const updated = { ...state.conversationCache, [conversationId]: message }

        commit('SET_MESSAGE_CACHE', updated)
        commit('SET_IS_SAVING_CACHE', true)

        await localStorage.setItem('conversation_cache', JSON.stringify(updated))
      } catch (error) {
        console.error(error)
      } finally {
        commit('SET_IS_SAVING_CACHE', false)
      }
    },

    async getCachedMessage({ state, commit }, conversationId) {
      const conversationCache = state.conversationCache
      try {
        if (conversationCache[conversationId]) {
          return conversationCache[conversationId]
        }

        const cache = await localStorage.getItem('conversation_cache')
        if (!cache) return undefined

        const parsedCache = JSON.parse(cache)
        commit('SET_MESSAGE_CACHE', { ...conversationCache, ...parsedCache })

        return parsedCache[conversationId] || undefined
      } catch (error) {
        console.error(error)
        return undefined
      }
    },

    async clearCache({ commit }) {
      try {
        commit('SET_MESSAGE_CACHE', {})
        await localStorage.removeItem('conversation_cache')
      } catch (error) {
        console.error(error)
      }
    },

    /**
     * The staged Idempotency-Key for one guest, if it is still usable.
     *
     * Why this is persisted at all: API Gateway abandons the issue request at 29s while
     * the Lambda may run to 90s and succeed, and the 120s rate-limit window lapses 30s
     * after that. An operator who reloads the tab and re-stages the card arrives with a
     * freshly minted key, so every per-request guard says "new request" and a second
     * real card is funded. Holding the key across the reload is what makes the retry a
     * replay instead.
     *
     * Scoped by customer, matching how the backend scopes both its claims — and by
     * amount, because replaying a key against a different body answers `409
     * idempotency-key-reuse` and issues nothing, which would strand the operator.
     */
    async getGiftCardIdempotencyKey({ state, commit }, { customerId, amountCents }) {
      try {
        if (!customerId) return undefined

        let entries = state.giftCardIdempotency
        if (!Object.keys(entries).length) {
          const stored = await localStorage.getItem(GIFT_CARD_IDEMPOTENCY_STORAGE_KEY)
          entries = stored ? pruneExpired(JSON.parse(stored)) : {}
          commit('SET_GIFT_CARD_IDEMPOTENCY', entries)
        }

        const entry = pruneExpired(entries)[customerId]
        if (!entry || entry.amountCents !== amountCents) return undefined

        return entry.key
      } catch (error) {
        // A key we cannot read is the pre-existing behaviour, not a new failure: the
        // caller mints a fresh one. Never throw — this runs while the operator is
        // picking an amount.
        console.error(error)
        return undefined
      }
    },

    async saveGiftCardIdempotencyKey({ commit, state }, { customerId, amountCents, key }) {
      try {
        if (!customerId || !key) return

        const updated = {
          ...pruneExpired(state.giftCardIdempotency),
          [customerId]: { key, amountCents, createdAt: Date.now() }
        }

        commit('SET_GIFT_CARD_IDEMPOTENCY', updated)
        await localStorage.setItem(GIFT_CARD_IDEMPOTENCY_STORAGE_KEY, JSON.stringify(updated))
      } catch (error) {
        console.error(error)
      }
    },

    /**
     * Called once the card exists — from the composer on a successful send, and from the
     * websocket handler for the send whose HTTP response never arrived.
     *
     * Load-bearing: a key left behind after a successful issuance would be replayed by
     * the operator's NEXT deliberate card for the same guest, and the backend would
     * answer with the first card's stored response. The UI would report success for a
     * card that was never issued.
     */
    async clearGiftCardIdempotencyKey({ commit, state }, customerId) {
      try {
        if (!customerId) return

        const updated = pruneExpired(state.giftCardIdempotency)
        delete updated[customerId]

        commit('SET_GIFT_CARD_IDEMPOTENCY', updated)
        await localStorage.setItem(GIFT_CARD_IDEMPOTENCY_STORAGE_KEY, JSON.stringify(updated))
      } catch (error) {
        console.error(error)
      }
    },

    async removeMessageCache({ commit, state }, conversationId) {
      try {
        const updated = { ...state.conversationCache }
        delete updated[conversationId]
        commit('SET_MESSAGE_CACHE', updated)
        await localStorage.setItem('conversation_cache', JSON.stringify(updated))
      } catch (error) {
        console.error(error)
      }
    }
  }
}
